<template>
  <div v-for="(message, index) in messages" :key="message.id" class="mb-4">
    <!-- Agent/AI Messages (Left side) -->
    <div v-if="message.type === 'agent' || message.type === 'ai'" class="flex">
      <!-- Dynamic Agent/AI Avatar -->
      <div
        class="w-8 h-8 rounded-full flex-shrink-0 mr-3 overflow-hidden"
        :class="message.type === 'ai' ? 'bg-blue-500' : 'bg-green-500'"
      >
        <!-- Show agent avatar if available (for human agents) -->
        <img
          v-if="
            message.type === 'agent' &&
            message.agentInfo &&
            message.agentInfo.avatar
          "
          :src="message.agentInfo.avatar"
          :alt="message.agentInfo.agentName || 'Agent'"
          class="w-full h-full object-cover"
        />
        <!-- Show AI avatar (for AI messages) -->
        <img
          v-else-if="message.type === 'ai'"
          src="../../assets/ChatlyAvatar.png"
          alt="NEX AI"
          class="w-full h-full object-cover"
        />
        <!-- Fallback for human agents without avatar -->
        <div
          v-else
          class="w-full h-full flex items-center justify-center text-white font-semibold text-xs"
        >
          {{ getAgentInitials(message.agentInfo?.agentName || "SA") }}
        </div>
      </div>

      <!-- Message Content with consistent width constraints -->
      <div class="max-w-xs md:max-w-md min-w-0 overflow-hidden">
        <!-- Typing Animation -->
        <div
          v-if="message.isTyping"
          class="bg-white rounded-lg rounded-tl-none p-3"
          style="border: 1px solid #f0f0f0"
        >
          <TypingIndicator />
        </div>

        <!-- Regular Message -->
        <div
          v-else
          class="bg-white rounded-lg rounded-tl-none p-3 overflow-hidden"
          style="border: 1px solid #f0f0f0"
        >
          <!-- FAQ Card Display - FIXED: Better overflow handling -->
          <div
            v-if="
              (message.message_type === 'faq' || message.faq_question) &&
              message.faq_question
            "
            class="mb-2 overflow-hidden"
          >
            <div
              @click="openFaq(message)"
              class="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors overflow-hidden"
            >
              <!-- FAQ Icon -->
              <svg
                class="w-6 h-6 text-gray-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>

              <!-- FAQ Content - FIXED: Single line with truncation -->
              <div class="flex-1 min-w-0 overflow-hidden">
                <!-- FAQ Question/Title - FIXED: Single line truncation -->
                <div class="text-sm font-medium text-gray-700 truncate">
                  {{ message.faq_question }}
                </div>
                <!-- FAQ Category -->
                <div class="text-xs text-gray-500 truncate">
                  {{ message.faq_category || "FAQ" }}
                </div>
              </div>

              <!-- Arrow Icon -->
              <svg
                class="w-4 h-4 text-gray-400 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </div>
          </div>

          <!-- File attachments - FIXED: Better overflow handling -->
          <div v-if="hasFileAttachment(message)" class="mb-2 overflow-hidden">
            <!-- GIF display - Already properly constrained -->
            <div
              v-if="
                message.gif_url ||
                message.gif_id ||
                message.mime_type === 'image/gif'
              "
              class="gif-container overflow-hidden"
            >
              <img
                :src="getImageUrl(message)"
                :alt="message.gif_title || message.file_name || 'GIF'"
                class="w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity gif-image block"
                style="
                  max-height: 200px;
                  object-fit: cover;
                  border: 2px solid transparent;
                  background: linear-gradient(
                        rgba(255, 255, 255, 0.1),
                        rgba(255, 255, 255, 0.1)
                      )
                      padding-box,
                    linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)
                      border-box;
                  border-radius: 8px;
                "
                @click="
                  openImageModal(
                    getImageUrl(message),
                    message.gif_title || message.file_name
                  )
                "
                @load="onImageLoad(message)"
                @error="onImageError(message, $event)"
              />
              <div
                v-if="
                  message.gif_title || message.file_name || message.file_size
                "
                class="text-xs opacity-75 mt-1 overflow-hidden"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center min-w-0 flex-1 overflow-hidden">
                    <span class="inline-flex items-center mr-1 flex-shrink-0">🎬</span>
                    <span class="truncate text-xs">{{
                      message.gif_title || message.file_name
                    }}</span>
                  </div>
                  <div v-if="message.file_size" class="ml-1 flex-shrink-0">
                    <span class="text-xs whitespace-nowrap">{{
                      formatFileSize(message.file_size)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Regular Image files - Already properly constrained -->
            <div v-else-if="isImageFile(message)" class="overflow-hidden">
              <img
                :src="getImageUrl(message)"
                :alt="message.file_name || 'Uploaded image'"
                class="w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity block"
                style="
                  max-height: 200px;
                  object-fit: cover;
                  background-color: #f3f4f6;
                "
                @click="openImageModal(getImageUrl(message), message.file_name)"
                @load="onImageLoad(message)"
                @error="onImageError(message, $event)"
              />

              <div
                v-if="message.file_name || message.file_size"
                class="text-xs opacity-75 mt-1 overflow-hidden"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center min-w-0 flex-1 overflow-hidden">
                    <span class="inline-flex items-center mr-1 text-blue-400 flex-shrink-0"
                      >📷</span
                    >
                    <span class="truncate text-gray-500 text-xs">{{
                      message.file_name
                    }}</span>
                  </div>
                  <div v-if="message.file_size" class="ml-1 flex-shrink-0">
                    <span class="text-gray-500 text-xs whitespace-nowrap">{{
                      formatFileSize(message.file_size)
                    }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Other file types - FIXED: Better overflow handling -->
            <div
              v-else
              class="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 overflow-hidden"
            >
              <svg
                class="w-6 h-6 text-gray-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              <div class="flex-1 min-w-0 overflow-hidden">
                <div class="text-sm font-medium text-gray-700 truncate">
                  {{ message.file_name || "File" }}
                </div>
                <div class="text-xs text-gray-500 truncate">
                  {{ formatFileSize(message.file_size) }}
                </div>
              </div>

              <a
                v-if="message.download_url"
                :href="message.download_url"
                :download="message.file_name"
                class="text-xs text-blue-600 hover:text-blue-800 underline flex-shrink-0 whitespace-nowrap"
                target="_blank"
              >
                Download
              </a>
            </div>
          </div>

          <!-- Message content with proper line breaks - FIXED: Keep break-words for message text -->
          <p
            v-if="
              message.content &&
              message.content !== '📷 Image' &&
              message.content !== '📋 FAQ'
            "
            class="text-gray-800 text-sm whitespace-pre-line break-words overflow-hidden"
          >
            {{ message.content }}
          </p>

          <!-- Show sender name -->
          <span class="text-xs text-gray-500 mt-1 block truncate">
            {{ getSenderName(message) }}
          </span>
        </div>
      </div>
    </div>

    <!-- Customer Messages (Right side) -->
    <div v-else-if="message.type === 'customer'" class="flex justify-end">
      <div class="max-w-xs md:max-w-md min-w-0 overflow-hidden">
        <div
          class="rounded-lg rounded-tr-none p-3 text-white overflow-hidden"
          :style="{
            backgroundColor: styles.buttonColor || '#000000',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }"
        >
          <!-- Customer file attachments - FIXED: Better overflow handling -->
          <div v-if="hasFileAttachment(message)" class="mb-2 overflow-hidden">
            <!-- GIF display - Already properly constrained -->
            <div
              v-if="
                message.gif_url ||
                message.gif_id ||
                message.mime_type === 'image/gif'
              "
              class="rounded-lg overflow-hidden gif-container"
            >
              <img
                :src="getImageUrl(message)"
                :alt="message.gif_title || message.file_name || 'GIF'"
                class="w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity gif-image"
                style="
                  max-height: 200px;
                  object-fit: cover;
                  border: 2px solid transparent;
                  background: linear-gradient(
                        rgba(255, 255, 255, 0.1),
                        rgba(255, 255, 255, 0.1)
                      )
                      padding-box,
                    linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #96ceb4)
                      border-box;
                  border-radius: 8px;
                "
                @click="
                  openImageModal(
                    getImageUrl(message),
                    message.gif_title || message.file_name
                  )
                "
                @load="onImageLoad(message)"
                @error="onImageError(message, $event)"
              />
              <div
                v-if="
                  message.gif_title || message.file_name || message.file_size
                "
                class="text-xs opacity-75 mt-1 space-y-1 overflow-hidden"
              >
                <div
                  v-if="message.gif_title || message.file_name"
                  class="flex items-center overflow-hidden"
                >
                  <span class="inline-flex items-center mr-2 flex-shrink-0">🎬</span>
                  <span class="truncate"
                    >{{
                      (message.gif_title || message.file_name).substring(0, 30)
                    }}{{
                      (message.gif_title || message.file_name).length > 30
                        ? "..."
                        : ""
                    }}</span
                  >
                </div>
                <div v-if="message.file_size" class="text-right">
                  <span class="whitespace-nowrap">{{ formatFileSize(message.file_size) }}</span>
                </div>
              </div>
            </div>

            <!-- Regular Image files - Already properly constrained -->
            <div
              v-else-if="isImageFile(message)"
              class="rounded-lg overflow-hidden"
            >
              <img
                :src="getImageUrl(message)"
                :alt="message.file_name || 'Uploaded image'"
                class="w-full h-auto rounded cursor-pointer hover:opacity-90 transition-opacity"
                style="
                  max-height: 200px;
                  object-fit: cover;
                  background-color: rgba(255, 255, 255, 0.1);
                "
                @click="openImageModal(getImageUrl(message), message.file_name)"
                @load="onImageLoad(message)"
                @error="onImageError(message, $event)"
              />

              <div
                v-if="message.file_name || message.file_size"
                class="text-xs opacity-75 mt-1 overflow-hidden"
              >
                <div class="flex items-center justify-between">
                  <div class="flex items-center min-w-0 flex-1 overflow-hidden">
                    <span class="inline-flex items-center mr-2 flex-shrink-0">📷</span>
                    <span class="truncate">{{ message.file_name }}</span>
                  </div>
                  <div v-if="message.file_size" class="ml-2 flex-shrink-0">
                    <span class="whitespace-nowrap">{{ formatFileSize(message.file_size) }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Other file types - FIXED: Better overflow handling -->
            <div
              v-else
              class="flex items-center space-x-3 p-3 rounded-lg bg-gray-50 overflow-hidden"
            >
              <svg
                class="w-6 h-6 text-gray-500 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"
                />
              </svg>
              <div class="flex-1 min-w-0 overflow-hidden">
                <div class="text-sm font-medium text-gray-700 truncate">
                  {{ message.file_name || "File" }}
                </div>
                <div class="text-xs text-gray-500 truncate">
                  {{ formatFileSize(message.file_size) }}
                </div>
              </div>

              <a
                v-if="message.download_url"
                :href="message.download_url"
                :download="message.file_name"
                class="text-xs text-blue-600 hover:text-blue-800 underline flex-shrink-0 whitespace-nowrap"
                target="_blank"
              >
                Download
              </a>
            </div>
          </div>

          <!-- Text content - FIXED: Better text wrapping -->
          <p
            v-if="message.content && message.content !== '📷 Image'"
            class="text-sm break-words overflow-hidden"
          >
            {{ message.content }}
          </p>
        </div>
      </div>
    </div>

    <!-- Agent Joined Banner (Center) -->
    <div
      v-else-if="message.type === 'agent-joined'"
      class="flex justify-center mb-4"
    >
      <div class="max-w-md w-full">
        <div
          class="bg-green-50 rounded-lg p-4"
          style="border: 1px solid #e0f2e0"
        >
          <div class="flex items-center justify-center mb-3">
            <!-- Agent Avatar -->
            <div
              class="w-12 h-12 rounded-full bg-green-500 flex-shrink-0 overflow-hidden"
              style="border: 2px solid #bbf7d0"
            >
              <img
                v-if="message.agentInfo && message.agentInfo.avatar"
                :src="message.agentInfo.avatar"
                :alt="message.agentInfo.agentName || 'Agent'"
                class="w-full h-full object-cover"
              />
              <div
                v-else
                class="w-full h-full flex items-center justify-center text-white font-semibold"
              >
                {{ getAgentInitials(message.agentInfo?.agentName) }}
              </div>
            </div>
          </div>
          <p class="text-green-700 text-sm font-medium text-center break-words">
            {{ message.content }}
          </p>
          <p class="text-green-600 text-xs mt-1 text-center break-words">
            {{ message.subtitle }}
          </p>
        </div>
      </div>
    </div>

    <!-- System Messages (Center) -->
    <div v-else-if="message.type === 'system'" class="flex justify-center">
      <div class="max-w-xs md:max-w-md">
        <!-- Status change messages with special styling -->
        <div
          v-if="message.statusChange"
          class="text-sm rounded-lg px-4 py-2 text-center font-medium break-words"
          :style="getStatusMessageStyle(message.statusChange.to)"
        >
          <p>{{ message.content }}</p>
        </div>
        <!-- Regular system messages -->
        <div
          v-else
          class="bg-gray-100 text-gray-600 text-sm rounded-md px-3 py-1 italic text-center break-words"
          style="border: 1px solid #f0f0f0"
        >
          <p>{{ message.content }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import TypingIndicator from "../TypingIndicator.vue";

export default {
  name: "ChatMessageRenderer",
  components: {
    TypingIndicator,
  },
  props: {
    messages: {
      type: Array,
      required: true,
      default: () => [],
    },
    styles: {
      type: Object,
      required: true,
      default: () => ({ buttonColor: "#000000" }),
    },
  },
  emits: ["open-faq"], // 🆕 NEW: Add FAQ event emission
  mounted() {
    // Debug: Log all messages to see their structure
    console.log(
      "🔍 All messages in ChatMessageRenderer:",
      this.messages.map((msg) => ({
        id: msg.id,
        type: msg.type,
        content: msg.content,
        hasImageUrl: !!msg.image_url,
        hasFileName: !!msg.file_name,
        mimeType: msg.mime_type,
        imageUrl: msg.image_url,
        fileId: msg.image_file_id,
        downloadUrl: msg.download_url,
        fileSize: msg.file_size,
        message_type: msg.message_type,
        // FAQ fields
        faq_id: msg.faq_id,
        faq_question: msg.faq_question,
        faq_answer: msg.faq_answer,
        faq_category: msg.faq_category,
      }))
    );
  },
  methods: {
    getSenderName(message) {
      if (message.type === "ai") {
        return "NEX AI";
      } else if (message.type === "agent") {
        return message.agentInfo?.agentName || "Support Agent";
      }
      return "Unknown";
    },

    getAgentInitials(agentName) {
      if (!agentName) return "A";
      return agentName
        .split(" ")
        .map((name) => name[0])
        .join("")
        .toUpperCase()
        .substring(0, 2);
    },

    // Check if message has any file attachment
    hasFileAttachment(message) {
      const hasAttachment = !!(
        message.image_url ||
        message.file_name ||
        message.image_file_id ||
        message.download_url
      );

      console.log(`📎 Message ${message.id} has attachment:`, {
        hasAttachment,
        image_url: !!message.image_url,
        file_name: !!message.file_name,
        image_file_id: !!message.image_file_id,
        download_url: !!message.download_url,
        mime_type: message.mime_type,
      });

      return hasAttachment;
    },

    // Check if file is an image
    isImageFile(message) {
      const isImage =
        message.mime_type && message.mime_type.startsWith("image/");
      console.log(`🖼️ Message ${message.id} is image:`, {
        isImage,
        mime_type: message.mime_type,
      });
      return isImage;
    },

    // Get the best image URL with fallbacks
    getImageUrl(message) {
      // Try different URL options in priority order
      const options = [
        this.getPreviewUrl(message), // Generated preview URL (best performance)
        message.image_url, // Original view URL from database
        message.download_url, // Download URL as last resort
      ].filter(Boolean); // Remove null/undefined values

      const selectedUrl = options[0];

      console.log("🖼️ Getting image URL for message:", {
        messageId: message.id,
        fileName: message.file_name,
        fileId: message.image_file_id,
        mimeType: message.mime_type,
        availableOptions: {
          preview: this.getPreviewUrl(message),
          image_url: message.image_url,
          download_url: message.download_url,
        },
        selectedUrl: selectedUrl,
      });

      return selectedUrl;
    },

    // Generate preview URL for better performance
    getPreviewUrl(message) {
      if (!message.image_file_id) {
        console.log("❌ No image_file_id for preview URL generation");
        return null;
      }

      // Remove mode=admin for public access in widget
      const baseUrl = "https://cloud.appwrite.io/v1";
      const projectId = "67e12daa0002eec157ab";
      const bucketId = "6864e70f0014aeb0920f";

      const previewUrl = `${baseUrl}/storage/buckets/${bucketId}/files/${message.image_file_id}/preview?project=${projectId}&width=400&height=300&quality=80`;

      console.log("🔗 Generated preview URL:", previewUrl);
      return previewUrl;
    },

    // Handle successful image load
    onImageLoad(message) {
      console.log("✅ Image loaded successfully:", {
        messageId: message.id,
        fileName: message.file_name,
        fileId: message.image_file_id,
      });
    },

    // Handle image loading errors with fallback logic
    onImageError(message, event) {
      console.error("❌ Image failed to load:", {
        messageId: message.id,
        fileName: message.file_name,
        fileId: message.image_file_id,
        attemptedUrl: event.target.src,
        errorType: event.type,
        allUrls: {
          image_url: message.image_url,
          download_url: message.download_url,
          generated_preview: this.getPreviewUrl(message),
        },
      });

      // Try fallback URLs
      const img = event.target;
      const currentSrc = img.src;

      if (currentSrc === this.getPreviewUrl(message) && message.image_url) {
        console.log("🔄 Falling back to image_url");
        img.src = message.image_url;
      } else if (currentSrc === message.image_url && message.download_url) {
        console.log("🔄 Falling back to download_url");
        img.src = message.download_url;
      } else {
        console.log("❌ All image URL options failed");
      }
    },

    // 🆕 ENHANCED: FAQ navigation with emission to parent
    // In ChatMessageRenderer.vue - Fixed openFaq method
    openFaq(message) {
      console.log("📋 Opening FAQ:", {
        faq_id: message.faq_id,
        question: message.faq_question,
        answer: message.faq_answer,
        category: message.faq_category,
        message_type: message.message_type,
        full_message: message, // Log the entire message for debugging
      });

      // ✅ FIXED: More flexible validation - check for either faq_id OR faq_question
      if ((!message.faq_id && !message.faq_question) || !message.faq_question) {
        console.error("❌ Invalid FAQ data - missing required fields:", {
          has_faq_id: !!message.faq_id,
          has_faq_question: !!message.faq_question,
          message_type: message.message_type,
          message,
        });
        alert("Dieser FAQ-Artikel ist nicht verfügbar.");
        return;
      }

      // ✅ FIXED: Create FAQ object with fallback for missing faq_id
      const faqData = {
        $id: message.faq_id || `faq_${Date.now()}`, // Fallback ID if missing
        question: message.faq_question,
        answer: message.faq_answer,
        category: message.faq_category || "General",
      };

      console.log("✅ FAQ data prepared:", faqData);

      // 🎯 EMIT EVENT TO PARENT COMPONENT
      this.$emit("open-faq", {
        faqData: faqData,
        category: message.faq_category,
        action: "navigate-to-faq",
        directToFaq: true,
      });

      console.log("🎯 FAQ navigation event emitted");
    },

    // 🆕 NEW: Get plain text preview from HTML content
    // 🆕 IMPROVED: Get plain text preview from HTML content with better truncation
    getPlainTextPreview(html) {
      if (!html) return "";

      // Remove HTML tags
      const withoutTags = html.replace(/<[^>]*>/g, " ");

      // Decode HTML entities using a temporary textarea element
      const textarea = document.createElement("textarea");
      textarea.innerHTML = withoutTags;
      const decoded = textarea.value;

      // Clean up extra whitespace and line breaks
      const cleaned = decoded
        .replace(/\s+/g, " ") // Replace multiple spaces with single space
        .replace(/\n+/g, " ") // Replace line breaks with spaces
        .trim();

      // Truncate to a reasonable length for preview (reduced from 80 to 60)
      return cleaned.length > 60 ? cleaned.substring(0, 60) + "..." : cleaned;
    },

    // Format file size for display
    formatFileSize(bytes) {
      if (!bytes) return "0 Bytes";

      const k = 1024;
      const sizes = ["Bytes", "KB", "MB", "GB"];
      const i = Math.floor(Math.log(bytes) / Math.log(k));

      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    },

    // Open image in new tab
    openImageModal(imageUrl, fileName) {
      if (imageUrl) {
        console.log("🖼️ Opening image:", { imageUrl, fileName });
        window.open(imageUrl, "_blank");
      }
    },

    // Return inline styles for Shadow DOM compatibility
    getStatusMessageStyle(status) {
      switch (status) {
        case "paused":
          return {
            backgroundColor: "#fefce8",
            color: "#a16207",
            border: "1px solid #fde047",
          };
        case "ended":
        case "closed":
          return {
            backgroundColor: "#fef2f2",
            color: "#b91c1c",
            border: "1px solid #fecaca",
          };
        case "active":
          return {
            backgroundColor: "#f0fdf4",
            color: "#15803d",
            border: "1px solid #bbf7d0",
          };
        case "resolved":
          return {
            backgroundColor: "#eff6ff",
            color: "#1d4ed8",
            border: "1px solid #93c5fd",
          };
        default:
          return {
            backgroundColor: "#f9fafb",
            color: "#374151",
            border: "1px solid #e5e7eb",
          };
      }
    },
  },
};
</script>

<style scoped>
/* GIF specific styling with gradient border - Inline styles for Shadow DOM compatibility */
.gif-container {
  position: relative;
}

/* Hover animation for GIF container */
.gif-container:hover .gif-image {
  transform: scale(1.02);
  transition: transform 0.2s ease-in-out;
}

/* Line clamp for FAQ preview */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Enhanced line clamp for FAQ content */
.line-clamp-1 {
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

.line-clamp-3 {
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Hover effect for FAQ cards */
.faq-card:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}
</style>
